<template>
  <div ref="tableContainer" class="draft-index">
    <el-row class="page-buttons">
      <editor-button
        :icon-only="true"
        :loading="loading"
        :tooltip="$t('orderDraft.addLine')"
        button-icon="el-icon-circle-plus"
        action="primary"
        style="float:left;"
        @click="addOrderLine()"
      />
      <editor-button
        :icon-only="false"
        :loading="loading"
        action="primary"
        button-translation="orderDraft.placeOrder"
        style="float:right;"
        @click="openOrderDialog"
      />
      <editor-button
        :icon-only="true"
        :loading="loading"
        :tooltip="$t('orderDraft.consumerData')"
        action="primary"
        button-icon="el-icon-user-solid"
        style="float:right;"
        @click="enterConsumerData"
      />
      <editor-button
        :icon-only="true"
        :loading="loading"
        :tooltip="$t('orderDraft.saveDraft')"
        action="save"
        style="float:right;"
        @click="saveDraft()"
      />
      <el-collapse-transition>
        <el-alert v-if="errorMsg" :title="errorMsg" type="error" :closable="false" />
      </el-collapse-transition>
    </el-row>
    <el-table
      ref="orderDraftOverview"
      v-loading="loading"
      :data="orderDraft.orderLines"
      border
      fit
      highlight-current-row
      style="width:100%;padding-top:0px"
      height="350"
      :row-class-name="tableRowClassName"
      @row-click="openEditorForRow"
    >
      <el-table-column label="#" width="30px">
        <template slot-scope="scope">
          <span>{{ getAndSetIndex(scope) }}</span>
        </template>
      </el-table-column>
      <el-table-column :label="$t('orderLine.customerReference')" width="150px">
        <template slot-scope="scope">
          <MDinput
            :value="scope.row.customerReference"
            :maxlength="20"
            type="text"
            style="width:97%"
            @input="customerReferenceInput($event, scope.row)"
          />
        </template>
      </el-table-column>
      <el-table-column :label="$t('interiorDecorator.productType')">
        <template slot-scope="scope">
          <Select
            v-show="scope.row.productGroup === undefined || scope.row.productGroup === null"
            v-model="scope.row['productGroup']"
            style="padding-left:0px !important"
            :required="true"
            :clearable="false"
            :translate-labels="true"
            :placeholder="$t('interiorDecorator.productType')"
            :options="PRODUCT_GROUPS"
            value-field="value"
            label-field="label"
            @input="onProductGroupSelected(scope.row)"
          />
          <span v-if="scope.row.productGroup !== undefined && scope.row.productGroup !== null">
            {{ $t(selectedProductGroupLabel(scope.row)) }}
          </span>
        </template>
      </el-table-column>
      <el-table-column :label="$t('orderLine.fabricName')">
        <template slot-scope="scope">
          {{ scope.row.fabricDescription }}
        </template>
      </el-table-column>
      <el-table-column :label="$t('orderLine.liningFabricProduct')">
        <template slot-scope="scope">
          {{ scope.row.liningDescription }}
        </template>
      </el-table-column>
      <el-table-column :label="$t('interiorDecorator.finish')">
        <template slot-scope="scope">
          {{ getGeneralFinish(scope.row) }}
        </template>
      </el-table-column>
      <el-table-column :label="$t('interiorDecorator.width')" width="100px">
        <template slot-scope="scope">
          <span style="display: flex;justify-content: center">{{ getWidth(scope.row) }}</span>
        </template>
      </el-table-column>
      <el-table-column :label="$t('interiorDecorator.height')" width="120px">
        <template slot-scope="scope">
          <span style="display: flex;justify-content: center">{{ getHeight(scope.row) }}</span>
        </template>
      </el-table-column>
      <el-table-column :label="$t('interiorDecorator.requiredFabric')" width="130px">
        <template slot-scope="scope">
          <span style="display: flex;justify-content: center">
            {{ scope.row.requiredFabricInCm || $t('draftTool.tbd') }}
          </span>
        </template>
      </el-table-column>
      <el-table-column :label="$t('orderLine.room')">
        <template slot-scope="scope">
          <MDinput v-model="scope.row.room" type="text" :maxlength="32" />
        </template>
      </el-table-column>
      <el-table-column :label="$t('table.actions')" align="center" width="140px">
        <template slot-scope="scope">
          <editor-button
            :tooltip="$t('common.copy')"
            action="primary"
            button-icon="el-icon-document-copy"
            @click="copyOrderLine(scope.row)"
          />
          <editor-button action="delete" @click="deleteOrderLine(scope.row)" />
        </template>
      </el-table-column>
    </el-table>
    <el-dialog :visible.sync="dialogVisible" :close-on-click-modal="false">
      <div style="overflow:hidden">
        <h1>{{ $t('orderLine.delivery') }}</h1>
        <Select
          v-model="orderDraft.deliverAtHomeYN"
          style="padding-left:0px !important"
          :required="true"
          :placeholder="$t('orderLine.delivery')"
          :options="[
            { value: true, label: $t('orderLine.deliver') },
            { value: false, label: $t('orderLine.pickUp') }
          ]"
          value-field="value"
          label-field="label"
        />
        <h1>{{ $t('order.consumerDetails') }}</h1>
        <MDinput v-model="orderDraft.consumer.name" :maxlength="32"> {{ $t('common.name') }} </MDinput>
        <div v-if="!orderDraft.consumer.name" class="error">{{ $t('validation.required') }}</div>
        <MDinput v-model="orderDraft.consumer.street" :maxlength="32"> {{ $t('common.streetName') }} </MDinput>
        <div v-if="isHomeDelivery && !orderDraft.consumer.street" class="error">{{ $t('validation.required') }}</div>
        <MDinput v-model="orderDraft.consumer.houseNumber" :maxlength="8"> {{ $t('common.streetNumber') }} </MDinput>
        <div v-if="isHomeDelivery && !orderDraft.consumer.houseNumber" class="error">
          {{ $t('validation.required') }}
        </div>
        <MDinput v-model="orderDraft.consumer.postalCode" :maxlength="7"> {{ $t('common.postalCode') }} </MDinput>
        <div v-if="isHomeDelivery && !orderDraft.consumer.postalCode" class="error">
          {{ $t('validation.required') }}
        </div>
        <MDinput v-model="orderDraft.consumer.city" :maxlength="32"> {{ $t('common.city') }} </MDinput>
        <div v-if="isHomeDelivery && !orderDraft.consumer.city" class="error">{{ $t('validation.required') }}</div>
        <MDinput v-model="orderDraft.consumer.email" type="email" :maxlength="64">{{ $t('common.email') }}</MDinput>
        <div v-if="isHomeDelivery && !orderDraft.consumer.email" class="error">{{ $t('validation.required') }}</div>
        <MDinput v-model="orderDraft.consumer.phone" :maxlength="32">{{ $t('common.phone') }}</MDinput>
        <div v-if="isHomeDelivery && !orderDraft.consumer.phone" class="error">{{ $t('validation.required') }}</div>
        <Select
          v-model="orderDraft.consumer.countryCode"
          style="margin-top:10px"
          :options="countries"
          value-field="code"
          label-field="name"
          required
        />
        <div :hidden="!isOrderButtonVisible">
          <editor-button
            style="float:right"
            :icon-only="false"
            action="primary"
            :disabled="!canOrder"
            :button-string="$t('orderDraft.send')"
            @click="placeOrder()"
          />
        </div>
        <editor-button
          :icon-only="false"
          :loading="loading"
          :button-string="$t('orderDraft.saveDraft')"
          action="save"
          style="float:right;margin-right:5px;"
          @click="saveDraft()"
        />
        &nbsp;
      </div>
    </el-dialog>
  </div>
</template>

<script>
import EditorButton from '@/components/crud/EditorButton';
import { closeCurrentTag } from '@/utils/tags';
import Select from '@/components/DSE/Select';
import MDinput from '@/components/MDinput';

import {
  BottomHem,
  DefaultBandSizeInCm,
  Fixture,
  InteriorDecoratorProductGroup,
  OperatingType,
  Pieces,
  PleatType,
  RibsEqual,
  RomanBlindChainColor,
  RomanBlindRibs,
  RomanBlindSubtype,
  RomanBlindSystemColor,
  EmbraceFinishes
} from './constants';

export default {
  name: 'DraftIndex',
  components: {
    EditorButton,
    Select,
    MDinput
  },
  props: {
    initializing: {
      type: Boolean,
      default: false
    },
    orderDraft: {
      type: Object
    },
    settings: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      waiting: false,
      errorMsg: '',
      dialogVisible: false,
      isOrderButtonVisible: false
    };
  },
  computed: {
    canOrder() {
      const orderDraft = this.orderDraft;
      const consumer = orderDraft.consumer;
      return (
        consumer.name &&
        (!orderDraft.deliverAtHomeYN ||
          (consumer.street &&
            consumer.houseNumber &&
            consumer.postalCode &&
            consumer.city &&
            consumer.email &&
            consumer.phone))
      );
    },
    countries() {
      return this.$store.getters['country/loadedItems'];
    },
    isHomeDelivery() {
      return this.orderDraft.deliverAtHomeYN;
    },
    loading() {
      return this.waiting || this.initializing;
    }
  },
  created() {
    const orderDraft = this.orderDraft;
    if (orderDraft && orderDraft.orderLines && orderDraft.orderLines.length === 0) {
      orderDraft.orderLines = [this.createOrderLineDraft(1)];
    }
    this.PRODUCT_GROUPS = [
      {
        value: InteriorDecoratorProductGroup.CurtainPanel,
        label: 'draftTool.productGroupCurtain'
      },
      {
        value: InteriorDecoratorProductGroup.CurtainRoomHigh,
        label: 'draftTool.productGroupRoomHighCurtain'
      },
      {
        value: InteriorDecoratorProductGroup.NetCurtainRoomHigh,
        label: 'draftTool.productGroupLaces'
      },
      {
        value: InteriorDecoratorProductGroup.RomanBlind,
        label: 'draftTool.productGroupRomanBlind'
      },
      {
        value: InteriorDecoratorProductGroup.TieBack,
        label: 'draftTool.productGroupTieBack'
      }
    ];
  },
  methods: {
    getAndSetIndex(scope) {
      const index = scope.$index + 1;
      scope.row.lineNumber = index;
      return index;
    },
    selectedProductGroupLabel(row) {
      return this.PRODUCT_GROUPS.find(pg => pg.value === row.productGroup).label;
    },
    getGeneralFinish(row) {
      switch (row.productGroup) {
        case InteriorDecoratorProductGroup.CurtainPanel:
        case InteriorDecoratorProductGroup.CurtainRoomHigh:
        case InteriorDecoratorProductGroup.NetCurtainRoomHigh:
          switch (row.curtain.fixture) {
            case Fixture.Rail:
            case Fixture.RodWithRings:
              switch (row.curtain.pleatType) {
                case PleatType.SinglePleat:
                  return this.$t('draftTool.pleatSingle');
                case PleatType.SingleAndReturnPleat:
                  return this.$t('draftTool.pleatSingleReturn');
                case PleatType.DoublePleat:
                  return this.$t('draftTool.pleatDouble');
                case PleatType.DoubleAndReturnPleat:
                  return this.$t('draftTool.pleatDoubleReturn');
                case PleatType.TriplePleat:
                  return this.$t('draftTool.pleatTriple');
                case PleatType.PleatingTape:
                  return this.$t('draftTool.pleatingTape');
                case PleatType.FlatPleat:
                  return this.$t('draftTool.pleatFlat');
                default:
                  return '';
              }
            case Fixture.Grommets:
              return this.$t('draftTool.fixtureGrommets');
            case Fixture.RodWithSlide:
              return this.$t('draftTool.fixtureTunnelAtTop');
            case Fixture.RodWithSlideOnSpan:
              return this.$t('draftTool.fixtureTunnelAtTopAndBottom');
            case Fixture.Wave:
              return this.$t('draftTool.fixtureWaveCurtain');
            case Fixture.Loop:
              return this.$t('draftTool.fixtureTabTop');
            default:
              return '';
          }
        case InteriorDecoratorProductGroup.RomanBlind:
          switch (row.romanBlind.subtype) {
            case RomanBlindSubtype.Roman2:
            case RomanBlindSubtype.Roman2WithoutRail:
              return 'Roman 2';
            case RomanBlindSubtype.Roman1:
              return 'Roman 1';
            default:
              return '';
          }
        case InteriorDecoratorProductGroup.TieBack:
          switch (row.embrace.finishCode) {
            case EmbraceFinishes.Banana.Code:
              return this.$t(EmbraceFinishes.Banana.TranslationString);
            case EmbraceFinishes.Straight.Code:
              return this.$t(EmbraceFinishes.Straight.TranslationString);
            default:
              return '';
          }
        default:
          return '';
      }
    },
    getHeight(row) {
      switch (row.productGroup) {
        case InteriorDecoratorProductGroup.CurtainPanel:
        case InteriorDecoratorProductGroup.CurtainRoomHigh:
        case InteriorDecoratorProductGroup.NetCurtainRoomHigh:
          return row.curtain.heightInCm;
        case InteriorDecoratorProductGroup.RomanBlind:
          return row.romanBlind.heightInCm;
        case InteriorDecoratorProductGroup.TieBack:
          switch (row.embrace.finishCode) {
            case EmbraceFinishes.Banana.Code:
              return EmbraceFinishes.Banana.HeightInCm;
            case EmbraceFinishes.Straight.Code:
              return EmbraceFinishes.Straight.HeightInCm;
            default:
              return '';
          }
        default:
          return '';
      }
    },
    getWidth(row) {
      switch (row.productGroup) {
        case InteriorDecoratorProductGroup.CurtainPanel:
        case InteriorDecoratorProductGroup.CurtainRoomHigh:
        case InteriorDecoratorProductGroup.NetCurtainRoomHigh:
          return row.curtain.railLengthInCm;
        case InteriorDecoratorProductGroup.RomanBlind:
          return row.romanBlind.widthInCm;
        case InteriorDecoratorProductGroup.TieBack:
          return row.embrace.widthInCm;
        default:
          return '';
      }
    },
    enterConsumerData() {
      this.dialogVisible = true;
      this.isOrderButtonVisible = false;
    },
    openOrderDialog() {
      let hasErrors = false;
      this.waiting = true;
      this.$store
        .dispatch('interiorDecoratorOrder/validate', this.orderDraft)
        .then(validationErrors => {
          this.orderDraft.orderLines.forEach(orderLine => {
            const errorsForOrderLine = validationErrors[orderLine.lineNumber];
            if (errorsForOrderLine) {
              orderLine.errors = errorsForOrderLine;
              if (errorsForOrderLine.length > 0) {
                hasErrors = true;
              }
              this.$refs.orderDraftOverview.setCurrentRow(orderLine);
            }
          });
          if (!hasErrors) {
            this.dialogVisible = true;
            this.isOrderButtonVisible = true;
          }
        })
        .finally(() => {
          this.waiting = false;
        });
    },
    tableRowClassName({ row, rowIndex }) {
      if (row.errors.length > 0) {
        return 'warning-row';
      }
      return '';
    },
    onProductGroupSelected(orderLineDraft) {
      this.applyProductGroupDefaults(orderLineDraft);
      this.openEditorForRow(orderLineDraft);
    },
    openEditorForRow(row) {
      if (row.productGroup !== undefined && row.productGroup !== null) {
        this.setCurrentRow(row);
        this.$emit('row-dblclick', row);
        this.$nextTick(() => this.$forceUpdate());
      } else {
        this.setCurrentRow(null);
        this.$emit('row-dblclick', null);
        this.$nextTick(() => this.$forceUpdate());
      }
    },
    applyProductGroupDefaults(orderLineDraft) {
      const options = this.getProductGroupOptions(orderLineDraft.productGroup);
      switch (orderLineDraft.productGroup) {
        case InteriorDecoratorProductGroup.CurtainPanel:
        case InteriorDecoratorProductGroup.CurtainRoomHigh:
        case InteriorDecoratorProductGroup.NetCurtainRoomHigh:
          if (options && options.bottomHemCode) {
            if (options.bottomHemCode === 'AFWLK') {
              orderLineDraft.curtain.leadCordYN = true;
            } else {
              orderLineDraft.curtain.bottomHem = this.getBottomHemOption(options.bottomHemCode);
              orderLineDraft.curtain.bottomHemSizeInCm = this.getBottomHemSize(options.bottomHemCode);
            }
          } else {
            orderLineDraft.curtain.bottomHem = undefined;
            orderLineDraft.curtain.bottomHemSizeInCm = undefined;
          }
          orderLineDraft.curtain.weightsYN = options && options.weightsYN;
          break;
      }
    },
    getProductGroupOptions(productGroup) {
      const code = this.getProductGroupCode(productGroup);
      return this.settings.productGroups.find(elem => elem.code === code);
    },
    getProductGroupCode(productGroup) {
      switch (productGroup) {
        case InteriorDecoratorProductGroup.CurtainPanel:
          return 'OG';
        case InteriorDecoratorProductGroup.CurtainRoomHigh:
          return 'KH';
        case InteriorDecoratorProductGroup.NetCurtainRoomHigh:
          return 'VI';
        case InteriorDecoratorProductGroup.RomanBlind:
          return 'VG';
        case InteriorDecoratorProductGroup.TieBack:
          return 'EB';
        default:
          return '';
      }
    },
    getBottomHemOption(bottomFinishCode) {
      switch (bottomFinishCode) {
        case 'AFWOZE':
        case 'AFWOZE12':
        case 'AFWOZE15':
        case 'AFWOZE20':
          return BottomHem.Single;
        case 'AFWOZD':
        case 'AFWOZD12':
        case 'AFWOZD15':
        case 'AFWOZD20':
          return BottomHem.Double;
        case 'AFWGOZ':
          return BottomHem.None;
        default:
          return undefined;
      }
    },
    getBottomHemSize(bottomFinishCode) {
      switch (bottomFinishCode) {
        case 'AFWOZE':
        case 'AFWOZD':
          return 10;
        case 'AFWOZE12':
        case 'AFWOZD12':
          return 12;
        case 'AFWOZE15':
        case 'AFWOZD15':
          return 15;
        case 'AFWOZE20':
        case 'AFWOZD20':
          return 20;
        case 'AFWGOZ':
          return 0;
        default:
          return undefined;
      }
    },
    setCurrentRow(row) {
      this.$refs.orderDraftOverview.setCurrentRow(row);
    },
    refreshList() {},
    close() {
      closeCurrentTag(this);
    },
    addOrderLine() {
      const highestLineNumber = this.getHighestLineNumber();
      const orderLineDraft = this.createOrderLineDraft(highestLineNumber + 1);
      this.orderDraft.orderLines.push(orderLineDraft);
    },
    getHighestLineNumber() {
      const orderLines = this.orderDraft.orderLines;
      if (orderLines.length === 0) {
        return 0;
      }
      let lineNumber = 1;
      orderLines.forEach(item => {
        if (lineNumber < item.lineNumber) {
          lineNumber = item.lineNumber;
        }
      });
      return lineNumber;
    },
    createOrderLineDraft(lineNumber) {
      return {
        lineNumber: lineNumber,
        customerReference: '',
        fabricProductID: undefined,
        fabricDescription: '',
        fabricProductTypeDescription: '',
        fabricWidthInCm: undefined,
        requiredFabricInCm: undefined,
        liningProductID: undefined,
        liningDescription: '',
        liningFabricProductTypeDescription: '',
        liningFabricWidthInCm: undefined,
        productGroup: null,
        errors: [],
        specials: [],
        curtain: {
          bandSizeInCm: DefaultBandSizeInCm,
          bottomHem: null,
          bottomHemSizeInCm: null,
          bracketsYN: true,
          cutSizeLeftInCm: null,
          cutSizeRightInCm: null,
          embraceFinish: '',
          fixture: undefined,
          grommetsProductID: null,
          headingHeightInCm: undefined,
          leadCordYN: false,
          minimumPleatFactor: undefined,
          maximumPleatFactor: undefined,
          liningCutSizeLeftInCm: null,
          liningCutSizeRightInCm: null,
          liningPanelsLeft: null,
          liningPanelsRight: null,
          panelsLeft: null,
          panelsRight: null,
          pieces: Pieces.TwoPieces,
          pleatFactor: undefined,
          pleatFactorLeft: undefined,
          pleatFactorRight: undefined,
          pleatType: undefined,
          pleatingTapeSizeInCm: 2.5,
          railLengthInCm: undefined,
          tunnelPercentage: undefined,
          tunnelSizeInCm: undefined,
          waveSizeInCm: null,
          weightsYN: false,
          widthLeftInCm: undefined,
          widthRightInCm: undefined
        },
        romanBlind: {
          assemblyHeightInCm: 225,
          chainColor: RomanBlindChainColor.White,
          cutSizeInCm: null,
          heightInCm: undefined,
          operatingSide: null,
          operatingType: OperatingType.Manual,
          liningCutSizeInCm: null,
          liningPanels: null,
          panels: null,
          subtype: RomanBlindSubtype.Roman2,
          systemColor: RomanBlindSystemColor.White,
          ribsEqual: RibsEqual.NoSet,
          ribs: RomanBlindRibs.TunnelBack,
          widthInCm: undefined
        },
        embrace: {
          widthInCm: undefined,
          finishCode: undefined
        }
      };
    },
    saveDraft() {
      const orderDraft = this.orderDraft;
      if (orderDraft) {
        this.waiting = true;
        this.$store
          .dispatch('orderDraft/persistItem', orderDraft)
          .then(data => {
            if (data && data.id) {
              orderDraft.id = data.id;
            }
            this.$store.dispatch('notify/success', { message: this.$i18n.t('orderDraft.draftSaved') });
            this.$emit('saved');
          })
          .finally(() => {
            this.waiting = false;
          });
      }
    },
    placeOrder() {
      this.waiting = true;
      const command = this.convertToCommand();
      this.$store
        .dispatch('interiorDecoratorOrder/create', command)
        .then(() => {
          this.$store.dispatch('notify/success', { message: this.$i18n.t('common.data_created') });
          this.$emit('saved');
          closeCurrentTag(this);
        })
        .finally(() => {
          this.waiting = false;
        });
    },
    convertToCommand() {
      const orderDraft = this.orderDraft;
      const command = {
        ...orderDraft,
        orderNumber: orderDraft.id
      };
      delete command.id;
      return command;
    },
    copyOrderLine(row) {
      const newRow = { ...row };
      if (newRow.curtain) {
        newRow.curtain = { ...row.curtain };
      }
      if (newRow.romanBlind) {
        newRow.romanBlind = { ...row.romanBlind };
      }
      if (newRow.embrace) {
        newRow.embrace = { ...row.embrace };
      }

      const highestLineNumber = this.getHighestLineNumber();
      newRow.lineNumber = highestLineNumber + 1;
      newRow.customerReference = '';
      this.orderDraft.orderLines.push(newRow);
    },
    deleteOrderLine(row) {
      this.$store
        .dispatch('notify/confirm', { message: this.$i18n.t('common.delete_record_warning') })
        .then(() => {
          this.orderDraft.orderLines.splice(this.orderDraft.orderLines.indexOf(row), 1);
          this.$emit('row-dblclick', undefined);
        })
        .catch(() => {});
    },
    customerReferenceInput(input, row) {
      row.customerReference = input;
    }
  }
};
</script>
<style lang="scss">
.draft-index {
  margin-bottom: 20px !important;

  .el-table .warning-row {
    background: #ffdbdb;
  }

  .validation-errors {
    display: block;
    color: #f56c6c;
    text-align: left;
    top: 100%;
  }

  .el-table .sel-row {
    background: #f0f9eb;
  }

  .el-table th {
    background: rgba(233, 239, 243, 0.97) !important;
  }

  .el-dialog__header {
    padding-top: 10px;
  }
  .el-dialog__body {
    padding: 0px 20px 10px 20px;
  }
  .error {
    color: red;
    margin-top: 5px;
  }
}
</style>
